import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";

interface MailPackage {
  id: string;
  deal_id: string;
  documents_included: string[];
  printed_date: string | null;
  mailed_date: string | null;
  tracking_number: string | null;
  notes: string | null;
  created_at: string;
  deals: {
    deal_title: string;
    seller_name: string;
  };
}

export function PackageHistory() {
  const [packages, setPackages] = useState<MailPackage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("mail_packages")
      .select(`
        *,
        deals (
          deal_title,
          seller_name
        )
      `)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setPackages(data as any);
    }
    setLoading(false);
  };

  const getDocumentLabel = (docId: string) => {
    const labels: Record<string, string> = {
      coverLetter: "Cover Letter",
      agreement: "Purchase Agreement",
      verification: "Contract Verification",
      respa: "RESPA Compliance",
      checklist: "Checklist Letter",
    };
    return labels[docId] || docId;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Package History</CardTitle>
          <CardDescription>Loading...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Package History</CardTitle>
        <CardDescription>Previously created mail packages</CardDescription>
      </CardHeader>
      <CardContent>
        {packages.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">
            No mail packages have been created yet
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Deal</TableHead>
                <TableHead>Seller</TableHead>
                <TableHead>Documents</TableHead>
                <TableHead>Printed</TableHead>
                <TableHead>Tracking</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.id}>
                  <TableCell className="font-medium">
                    {pkg.deals?.deal_title || "Unknown"}
                  </TableCell>
                  <TableCell>{pkg.deals?.seller_name || "Unknown"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {pkg.documents_included?.map((docId) => (
                        <Badge key={docId} variant="secondary" className="text-xs">
                          {getDocumentLabel(docId)}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {pkg.printed_date
                      ? format(new Date(pkg.printed_date), "MMM d, yyyy")
                      : "-"}
                  </TableCell>
                  <TableCell>{pkg.tracking_number || "-"}</TableCell>
                  <TableCell className="max-w-xs truncate">
                    {pkg.notes || "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
